/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './publisher-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {normalizeTargetingSearchPage} from '../normalize-targeting-search-page';
import {TargetingTemplateHook} from './targeting-template.component';
import {findCountryName, getCountryName} from '../../common/directives/country-select/countries';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {EarningsAccountApiService} from '../../earnings-account/earnings-account-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {SearchApiService} from '../../common/controller/search-api.service';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-publisher-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PublisherTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.publisher;
  @Input() underModel: any;
  @Input() modelLineItem: any;
  @Input() runningModel: any;
  @Input() mandatoryTargets: any;
  @Input() lineItemType: string;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  searchTerm = '';
  private readonly listState = signal<any>({searchResults: []}, {equal: () => false});
  get list(): any { return this.listState(); }
  set list(value: any) { this.listState.set(value); }
  filter: any = {};
  isPureMarketplace = false;
  hasCountries = false;
  hasCountriesTable = false;
  countryBuckets: any[] = [];
  private tabLoaded = false;
  private searchRequestId = 0;

  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    resolvedError: '',
    filterResolvedError: '',
    filterResolved: true,
    resolved: true,
    changePage: () => this.search(false, true)
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const model = this.model || {};
      const summary = (model.negated === true ? 'NOT: ' : '') + _.map(model.earningsAccounts, 'name').join(', ');
      return {
        vm: _.cloneDeep(model),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.publisher,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly earningsAccountApi: EarningsAccountApiService,
    private readonly searchApi: SearchApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnInit(): void {
    this.ensureModel();
    this.isPureMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.initCountryFilter();

    if (!this.tabController) {
      return;
    }

    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.publisher.widget, () => this.onTabSelect(), copyTemplate);
  }

  select(publisher: any): void {
    publisher.selected = true;
    this.model.earningsAccounts.push(_.cloneDeep(publisher));
  }

  deselect(publisher: any): void {
    _.remove(this.model.earningsAccounts, ['id', publisher.id]);
    if (this.list && this.list.searchResults) {
      (_.find(this.list.searchResults, ['id', publisher.id]) || {}).selected = false;
    }
  }

  search(newSearch?: boolean, filterSearch?: boolean): void {
    const metaResolvedParam = filterSearch ? 'filterResolved' : 'resolved';
    const metaResolvedErrorParam = metaResolvedParam + 'Error';
    this.searchMeta[metaResolvedParam] = false;
    this.searchMeta[metaResolvedErrorParam] = '';
    const requestId = ++this.searchRequestId;
    this.list = this.list;

    if (!filterSearch) {
      this.filter = {
        country: _.get(this.filter, ['country']) || null
      };
    }

    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    const searchParams: any = {
      q: this.searchTerm || '',
      types: 'EarningsAccount',
      objectState: 'ACTIVE',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1
    };
    if (_.get(this.modelLineItem, ['axProductIsHighImpact'])) {
      searchParams.isFullPage = true;
    }
    if (this.filter.country) {
      searchParams.country = this.filter.country;
    }
    if (this.lineItemType === 'POLITICAL') {
      searchParams.politicalAdsBlocked = false;
    }

    this.searchApi.search(searchParams).subscribe({
      next: (page) => {
        if (requestId !== this.searchRequestId) {
          return;
        }
        const list = normalizeTargetingSearchPage(page, {
          pageSize: this.searchMeta.pageSize,
          currentPage: this.searchMeta.currentPage,
          mapItem: (item: any) => {
            item.countriesExpanded = _.map(item.country || [], (country: string) => getCountryName(country)).join(', ');
            return item;
          }
        });
        const countryBuckets = this.normalizeBuckets(_.get(page, ['aggregations', 'country', 'buckets']));
        const isMandatory = (this.mandatoryTargets || []).indexOf(ADN_TARGETING_TYPES.siteCountry.id) > -1;
        const hasCountries = this.localNetworkInfo.getSiteCountries().length > 1 && !isMandatory &&
          !!(this.filter.country || !_.isEmpty(countryBuckets));
        const hasCountriesTable = this.localNetworkInfo.getSiteCountries().length > 1 &&
          !!(this.filter.country || !_.isEmpty(countryBuckets));
        if (hasCountries) {
          _.forEach(countryBuckets, (country: any) => {
            country.name = findCountryName(country.name);
          });
        }
        if (requestId !== this.searchRequestId) {
          return;
        }
        this.list = list;
        this.updateSelectedSearchResults();
        this.countryBuckets = countryBuckets;
        this.hasCountries = hasCountries;
        this.hasCountriesTable = hasCountriesTable;
        this.searchMeta[metaResolvedParam] = true;
      },
      error: (err: any) => {
        if (requestId !== this.searchRequestId) {
          return;
        }
        this.list = {searchResults: []};
        this.searchMeta[metaResolvedParam] = true;
        const statusText = _.get(err, ['statusText']);
        if (statusText) {
          this.searchMeta[metaResolvedErrorParam] = statusText;
        }
      }
    });
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search(false, true);
  }

  private onTabSelect(): void {
    if (this.tabLoaded) {
      return;
    }
    this.tabLoaded = true;
    this.search();
    this.hydratePublishers();
  }

  private hydratePublishers(): void {
    if (!this.model.earningsAccounts.length) {
      return;
    }
    const ids = _.map(this.model.earningsAccounts, 'id');
    const applyHydration = (page: any) => {
      const keyedResults = _.keyBy(page.results || page || [], 'id');
      this.model.earningsAccounts = _.map(this.model.earningsAccounts, (sel: any) => {
        const fullPublisherDetail = keyedResults[sel.id];
        if (!fullPublisherDetail) {
          return sel;
        }
        return {
          ...sel,
          ..._.pick(fullPublisherDetail, ['description', 'summary', 'politicalAdsApprovalRequired']),
          countriesExpanded: _.map(fullPublisherDetail.countries || [], (c: string) => getCountryName(c)).join(', ')
        };
      });
      this.list = this.list;
    };
    this.earningsAccountApi.getMany(ids).subscribe(applyHydration);
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    this.model.earningsAccounts = this.model.earningsAccounts || [];
    if (_.isNil(this.model.negated)) {
      this.model.negated = false;
    }
  }

  private initCountryFilter(): void {
    const countriesSelected = _.get(this.runningModel, [
      ADN_TARGETING_TYPES.siteCountry.targets,
      ADN_TARGETING_TYPES.siteCountry.apiProp
    ]);
    if (_.isArray(countriesSelected) && countriesSelected.length === 1) {
      this.filter = this.filter || {};
      this.filter.country = countriesSelected[0].toLowerCase();
    }
  }

  private updateSelectedSearchResults(): void {
    const selIds = _.keyBy(this.model.earningsAccounts, 'id');
    _.forEach(_.get(this.list, ['searchResults'], []), (a: any) => {
      a.selected = !!selIds[a.id];
    });
  }

  private normalizeBuckets(buckets: any): any[] {
    const bucketList = _.isArray(buckets) ? buckets : (_.isObject(buckets) ? _.values(buckets) : []);
    return _.filter(bucketList, (bucket: any) => {
      return bucket && (bucket.filter !== null || bucket.name !== null) &&
        (bucket.filter !== undefined || bucket.name !== undefined);
    });
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {earningsAccounts: []};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.ensureModel();
    this.updateSelectedSearchResults();
  }
}
